import type {
  BillingWalletUsageBalance,
  BillingWalletUsageMeter,
  BillingWalletUsageStorage,
} from '@customerio/hydra-ui-api';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';

export type WalletUsage =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      balance: BillingWalletUsageBalance;
      meters: Record<string, BillingWalletUsageMeter>;
      /** Null for an account without storage limits. */
      storage: BillingWalletUsageStorage | null;
      freeActiveProfiles: number | null;
    };

export const WALLET_USAGE_QUERY_KEY = ['getWalletUsage'];

function freeActiveProfiles(meters: Record<string, BillingWalletUsageMeter>): number | null {
  const grant = meters.active_profiles?.grant_total ?? 0;
  return grant > 0 ? grant : null;
}

/** The wallet's balance, its spend this billing period split by meter, and the account's storage now. */
export function useWalletUsage(): WalletUsage {
  const { account } = useAuth();
  const isFlex = isFlexPlan(account);

  const { data, isLoading } = useHydraUIAPIQuery({
    queryKey: [...WALLET_USAGE_QUERY_KEY, { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.billing.getWalletUsage({ params: { path: { account_id: String(account.id) } }, signal }),
    queryConfig: { enabled: isFlex },
  });

  // A failed refetch keeps the last data, so the surfaces that read it do not drop out.
  if (data != null) {
    return {
      status: 'ready',
      balance: data.balance,
      meters: data.meters,
      storage: data.storage ?? null,
      freeActiveProfiles: freeActiveProfiles(data.meters),
    };
  }
  if (isLoading) return { status: 'loading' };
  return { status: 'error' };
}

export function useFreeActiveProfiles(): number | null {
  const usage = useWalletUsage();
  return usage.status === 'ready' ? usage.freeActiveProfiles : null;
}
